/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */

/*
 * `Intl.Locale`'s week information is not in TypeScript's DOM or ES2022 libraries yet, and the
 * two shapes below are both in the wild: it shipped as a `weekInfo` getter and was then changed
 * to a `getWeekInfo()` method during standardisation. Declaring both here is what lets the call
 * site stay strict rather than reaching for `any`.
 */
interface WeekInfo {
    readonly firstDay?: number;
}

interface LocaleWithWeekInfo extends Intl.Locale {
    readonly weekInfo?: WeekInfo;
    getWeekInfo?: () => WeekInfo;
}

/**
 * The language the page is in, or undefined to let `Intl` fall back to the platform's own.
 *
 * Undefined and not a hardcoded tag: `Intl` treats the absent argument as "use the runtime
 * default", and that is a better guess than any single language this library could pick.
 */
export function pageLocale(): string | undefined {
    return document.documentElement.lang || undefined;
}

/**
 * Which day a week starts on, as a `Date.getDay()` index.
 *
 * Monday in most of the world this app is in, Sunday in some of it. `weekInfo` is the platform
 * answering that question; a fixed Monday is the fallback where it cannot. The modulo is because
 * `firstDay` counts Monday as 1 and Sunday as 7, while `getDay()` calls Sunday 0.
 */
export function firstWeekday(locale: string | undefined): number {
    try {
        const info = new Intl.Locale(locale ?? navigator.language) as LocaleWithWeekInfo;
        const week = typeof info.getWeekInfo === 'function' ? info.getWeekInfo() : info.weekInfo;
        if (week !== undefined && week.firstDay !== undefined) {
            return week.firstDay % 7;
        }
    } catch {
        /* An unknown language tag throws rather than falling back. */
    }

    return 1;
}
